import { useMemo } from "react";
import { useGlobalSettingsContext, useQuoteContext, useUserSettingsContext } from "../context";
import { getPackageLevels } from "../utils";
import {
	AssessmentOutlined,
	EventRepeatOutlined,
	MonetizationOnOutlined,
	PriceCheck
} from "@mui/icons-material";
import {
	ONE_TIME_NON_EXCLUDED_MARGIN_LABEL,
	ONE_TIME_NON_EXCLUDED_PROFIT_LABEL,
	getRecurringNonExcludedMarginLabel,
	getRecurringNonExcludedProfitLabel
} from "../common/marginViewLabels";
import {
	quoteHasExcludedFromCalculatedMarginTab,
	shouldShowOneTimeMarginViewMenu,
	shouldShowRecurringMarginViewMenu
} from "../common/marginViewVisibility";
import { hydrateClientNonExcludedMarginsWhenNoExcludedTabs } from "../common/hydrateNonExcludedMargins";
import {
	getNonExcludedRecurringGrossMarginDisplayForPeriod,
	getNonExcludedRecurringGrossProfitForPeriod,
	getRecurringGrossMarginForPeriod,
	getNonExcludedRecurringGrossMarginForPeriod,
	getRecurringGrossProfitForPeriod,
	normalizeRecurringHeaderType,
	resolveRecurringMarginPeriod
} from "../common/recurringHeaderMetrics";

export function useQuoteStatisticsData(isCollapsed) {
	const quote = useQuoteContext();
	const { hideProfit } = useUserSettingsContext();
	const {
		packageLevel,
		EnableOneTimeTotal,
		EnableOneTimeGrossProfit,
		EnableRecurring,
		EnableRecurringGrossProfit,
		EnableTotalQuoteValue,
		EnableDownpayment,
		QuoteRecurringType
	} = useGlobalSettingsContext();
	const packageLevels = getPackageLevels();
	const isNonPremiumPartner = packageLevel !== packageLevels?.premium;
	const recurringPrefix = normalizeRecurringHeaderType(QuoteRecurringType);

	const quoteStatisticsData = useMemo(() => {
		hydrateClientNonExcludedMarginsWhenNoExcludedTabs(quote);

		// Helper function to get recurring values based on prefix
		const getRecurringValue = (baseKey, prefixKey) => {
			// added this line bacsuse RecurringSubtotal AnnualRecurringSubTotal MonthlyRecurringSubTotal are not consistent
			const normalizedPrefixKey =
				prefixKey === "RecurringSubtotal" ? "RecurringSubTotal" : prefixKey;
			if (recurringPrefix === "Annual") {
				return quote[`Annual${normalizedPrefixKey}`];
			} else if (recurringPrefix === "Monthly") {
				return quote[`Monthly${normalizedPrefixKey}`];
			}
			return quote[baseKey];
		};

		// Get all recurring values
		const recurringTotal = getRecurringValue("RecurringTotal", "RecurringTotal");
		const recurringSubTotal = getRecurringValue("RecurringSubtotal", "RecurringSubtotal");
		const recurringTax = getRecurringValue("RecurringAllTax", "RecurringTax");
		const recurringCost = getRecurringValue("RecurringCost", "RecurringCost");
		const recurringGrossProfit = getRecurringValue(
			"RecurringGrossMarginAmount",
			"RecurringGrossProfit"
		);
		const recurringGrossMargin = getRecurringValue(
			"PrintedRecurringGrossMargin",
			"RecurringGrossMargin"
		);
		const recurringMarkup = getRecurringValue("PrintedRecurringMarkup", "RecurringMarkup");

		const anyTabExcludedFromCalculatedMargin = quoteHasExcludedFromCalculatedMarginTab(quote);
		const recurringMarginPeriod = resolveRecurringMarginPeriod(recurringPrefix, quote);
		const recurringGrossProfitForMarginMenu = getRecurringGrossProfitForPeriod(
			quote,
			recurringMarginPeriod
		);
		const recurringGrossMarginForMarginMenu = getRecurringGrossMarginForPeriod(
			quote,
			recurringMarginPeriod
		);
		const nonExcludedRecurringGrossProfitRaw = getNonExcludedRecurringGrossProfitForPeriod(
			quote,
			recurringMarginPeriod
		);
		const nonExcludedRecurringGrossMarginRaw = getNonExcludedRecurringGrossMarginForPeriod(
			quote,
			recurringMarginPeriod
		);
		const nonExcludedRecurringGrossProfit = anyTabExcludedFromCalculatedMargin
			? nonExcludedRecurringGrossProfitRaw
			: recurringGrossProfitForMarginMenu;
		const nonExcludedRecurringGrossMargin = anyTabExcludedFromCalculatedMargin
			? getNonExcludedRecurringGrossMarginDisplayForPeriod(quote, recurringMarginPeriod)
			: recurringGrossMargin;

		// Get recurring per cycle data
		const recurringPerCycle = getRecurringValue("RecurringPerCycle", "RecurringPerCycle") || {};
		const perCycleCount = Object.keys(recurringPerCycle).length;
		const showPopover = perCycleCount > 0;

		// Generate recurring title
		const getRecurringTitle = () => {
			if (recurringPrefix === "Annual" || recurringPrefix === "Monthly") {
				return `${recurringPrefix} Recurring (${perCycleCount})`;
			}
			return "Recurring";
		};

		const recurringTitle = getRecurringTitle();

		const showOneTimeMarginViewMenu = shouldShowOneTimeMarginViewMenu(
			quote,
			anyTabExcludedFromCalculatedMargin
		);
		const showRecurringMarginViewMenu = shouldShowRecurringMarginViewMenu(
			quote,
			recurringPrefix,
			anyTabExcludedFromCalculatedMargin,
			{
				recurringGrossProfit: recurringGrossProfitForMarginMenu,
				recurringGrossMargin: recurringGrossMarginForMarginMenu,
				nonExcludedRecurringGrossProfit: nonExcludedRecurringGrossProfitRaw,
				nonExcludedRecurringGrossMargin: nonExcludedRecurringGrossMarginRaw
			}
		);

		return [
			{
				key: "gross_profit",
				isHidden: hideProfit || !EnableOneTimeGrossProfit,
				value: [
					{
						title: "One Time Gross Profit",
						value: quote.formatCurrency(quote.GrossMarginAmount),
						icon: <AssessmentOutlined fontSize="medium" htmlColor="#1976d2" />,
						id: "test-one-time-gross-profit",
						other_values: [
							{
								sub_title: "Gross margin",
								subtitle_value: quote.PrintedGrossMargin,
								id: "test-one-time-gross-margin"
							},
							{
								sub_title: "Markup",
								subtitle_value: quote.PrintedMarkup,
								id: "test-one-time-markup"
							},
							{
								sub_title: "Disc Amt",
								subtitle_value: quote.formatCurrency(quote.SuggestedDiscountAmount),
								id: "test-one-time-discount"
							}
						],
						...(showOneTimeMarginViewMenu
							? {
									marginToggle: {
										nonExcludedFields: [
											{
												label: ONE_TIME_NON_EXCLUDED_PROFIT_LABEL,
												value: quote.formatCurrency(
													quote.NonExcludedGrossMarginAmount
												),
												id: "test-one-time-non-excluded-gross-profit"
											},
											{
												label: ONE_TIME_NON_EXCLUDED_MARGIN_LABEL,
												value: quote.PrintedNonExcludedGrossMargin,
												id: "test-one-time-non-excluded-gross-margin"
											}
										]
									}
								}
							: {})
					}
				]
			},
			{
				key: "recurring",
				isHidden: !EnableRecurring,
				value: [
					{
						title: recurringTitle,
						value: quote.formatCurrency(recurringTotal),
						key: "annual_recurring",
						showPopover: showPopover,
						icon: <EventRepeatOutlined fontSize="medium" htmlColor="#1976d2" />,
						id: "test-recurring",
						other_values: [
							{
								sub_title: "Subtotal",
								subtitle_value: quote.formatCurrency(recurringSubTotal),
								id: "test-recurring-subtotal"
							},
							{
								sub_title: "Est Tax",
								subtitle_value: quote.formatCurrency(recurringTax),
								id: "test-recurring-est-tax"
							},
							{
								sub_title: "Cost",
								subtitle_value: quote.formatCurrency(recurringCost),
								isHidden: hideProfit,
								id: "test-recurring-cost"
							}
						]
					}
				]
			},
			{
				key: "recurring_profit",
				isHidden: hideProfit || !EnableRecurringGrossProfit,
				value: [
					{
						title: `${
							recurringPrefix !== "Recurring" ? `${recurringPrefix} ` : ""
						}Recurring Gross Profit`,
						value: quote.formatCurrency(recurringGrossProfit),
						icon: <EventRepeatOutlined fontSize="medium" htmlColor="#1976d2" />,
						id: "test-recurring-gross-profit",
						isHidden: hideProfit || !EnableRecurringGrossProfit,
						other_values: [
							{
								sub_title: "Gross Margin",
								subtitle_value: recurringGrossMargin,
								id: "test-recurring-profit-gross-margin"
							},
							{
								sub_title: "Markup",
								subtitle_value: recurringMarkup,
								id: "test-recurring-profit-markup"
							},
							{
								sub_title: "Disc Amt",
								subtitle_value: quote.formatCurrency(
									quote.RecurringSuggestedDiscountAmount
								),
								id: "test-recurring-profit-discount"
							}
						],
						...(showRecurringMarginViewMenu
							? {
									marginToggle: {
										nonExcludedFields: [
											{
												label: getRecurringNonExcludedProfitLabel(
													recurringPrefix
												),
												value: quote.formatCurrency(
													nonExcludedRecurringGrossProfit
												),
												id: "test-recurring-non-excluded-gross-profit"
											},
											{
												label: getRecurringNonExcludedMarginLabel(
													recurringPrefix
												),
												value: nonExcludedRecurringGrossMargin,
												id: "test-recurring-non-excluded-gross-margin"
											}
										]
									}
								}
							: {})
					}
				]
			},
			{
				key: "down_payment_contract_value",
				isHidden: !(!isCollapsed && (EnableTotalQuoteValue || EnableDownpayment)),
				value: [
					{
						title: "Total Quote Value",
						value: `${quote.formatCurrency(quote?.TotalContractValue)}`,
						id: `test-total-quote-value`,
						isHidden: !EnableTotalQuoteValue,
						icon: <MonetizationOnOutlined fontSize="medium" htmlColor="#1976d2" />
					},
					{
						title: "Down Payment Amount",
						value: `${quote.formatCurrency(
							!quote?.OrderPorterAmount ? 0 : quote?.OrderPorterAmount
						)}`,
						id: `test-down-payment-amount`,
						icon: <PriceCheck fontSize="medium" htmlColor="#1976d2" />,
						isHidden: isCollapsed || isNonPremiumPartner || !EnableDownpayment
					}
				]
			},
			{
				key: "total_quote_value",
				isHidden: !isCollapsed || !EnableTotalQuoteValue,
				value: [
					{
						title: "Total Quote Value",
						value: `${quote.formatCurrency(quote?.TotalContractValue)}`,
						id: `test-total-quote-value`,
						icon: <MonetizationOnOutlined fontSize="medium" htmlColor="#1976d2" />
					}
				]
			},
			{
				key: "downpayment",
				isHidden: !isCollapsed || isNonPremiumPartner || !EnableDownpayment,
				value: [
					{
						title: "Down Payment Amount",
						value: `${quote.formatCurrency(
							!quote?.OrderPorterAmount ? 0 : quote?.OrderPorterAmount
						)}`,
						id: `test-down-payment-amount`,
						icon: <PriceCheck fontSize="medium" htmlColor="#1976d2" />
					}
				]
			}
		];
	}, [
		quote.GrossMarginAmount,
		quote?.OrderPorterAmount,
		quote?.TotalContractValue,
		quote?.RecurringTotal,
		quote?.RecurringGrossMarginAmount,
		quote?.DefaultRecurringTermType,
		quote?.RecurringSubtotal,
		quote?.RecurringAllTax,
		quote?.RecurringCost,
		quote?.SuggestedDiscountAmount,
		quote?.PrintedGrossMargin,
		quote?.PrintedMarkup,
		quote?.PrintedNonExcludedGrossMargin,
		quote?.NonExcludedGrossMarginAmount,
		quote?.RecurringSuggestedDiscountAmount,
		quote?.PrintedRecurringGrossMargin,
		quote?.PrintedRecurringMarkup,
		quote.QuoteTotal,
		quote?.BaseCurrency,
		hideProfit,
		isCollapsed,
		EnableOneTimeTotal,
		EnableOneTimeGrossProfit,
		EnableRecurring,
		EnableRecurringGrossProfit,
		EnableTotalQuoteValue,
		EnableDownpayment,
		quote?.AnnualRecurringTax,
		quote?.AnnualRecurringSubTotal,
		quote?.AnnualRecurringTotal,
		quote?.AnnualRecurringCost,
		quote?.AnnualRecurringGrossProfit,
		quote?.AnnualRecurringGrossMargin,
		quote?.AnnualRecurringMarkup,
		quote?.MonthlyRecurringTax,
		quote?.MonthlyRecurringSubTotal,
		quote?.MonthlyRecurringTotal,
		quote?.MonthlyRecurringCost,
		quote?.MonthlyRecurringGrossProfit,
		quote?.MonthlyRecurringGrossMargin,
		quote?.MonthlyRecurringMarkup,
		quote?.NonExcludedAnnualRecurringGrossProfit,
		quote?.NonExcludedMonthlyRecurringGrossProfit,
		quote?.PrintedNonExcludedAnnualRecurringGrossMargin,
		quote?.PrintedNonExcludedMonthlyRecurringGrossMargin,
		quote?.AnnualRecurringPerCycle,
		quote?.MonthlyRecurringPerCycle,
		QuoteRecurringType,
		quote?.Tabs
	]);

	return { recurringPrefix, quoteStatisticsData, isNonPremiumPartner };
}
